import _upperFirst from 'lodash/upperFirst';

import type { UseTableProps } from '@sravni/react-design-system/lib/Table';

import { makeCommonColumn } from '@components/RatingTable/columns/commonColumn';
import { makeOrdinalNumberColumn } from '@components/RatingTable/columns/ordinalNumberColumn';
import { makeOrganizationNameColumn } from '@components/RatingTable/columns/organizationNameColumn';
import { makeProductNameColumn } from '@components/RatingTable/columns/productNameColumn';
import { makeProductTermColumn } from '@components/RatingTable/columns/productTermColumn';
import { makeRatingColumn } from '@components/RatingTable/columns/ratingColumn';
import { makeReviewCountColumn } from '@components/RatingTable/columns/reviewCountColumn';
import { sortTermsByPopularity } from '@components/RatingTable/helpers/table';

import { getCoreRowModel, getSortedRowModel } from '@tanstack/react-table';

import { RATING_TYPES } from '../../constants/filters';
import type { TFilters } from '../../types/filters';
import type { OrganizationRatingItem, ProductRatingItem, RatingItem } from '../../types/rating';

const idColumn = {
  accessorKey: 'id',
  header: '',
  enableSorting: false,
  cell: (info) => makeOrdinalNumberColumn<RatingItem>(info),
  size: 15,
};

const ratingColumn = {
  accessorKey: 'rating',
  header: 'Рейтинг',
  cell: (info) => makeRatingColumn<RatingItem>(info),
  size: 100,
};

const reviewCountColumn = ({ filters }: { filters: TFilters }) => ({
  accessorKey: 'reviewCount',
  header: 'Отзывы',
  cell: (info) => makeReviewCountColumn({ filters })<RatingItem>(info),
  size: 100,
});

const responseCountColumn = {
  accessorKey: 'responseCount',
  header: 'Официальный ответ',
  cell: (info) => makeCommonColumn<RatingItem>(info),
  size: 220,
};

const solvedCountColumn = {
  accessorKey: 'solvedCount',
  header: 'Решено проблем',
  cell: (info) => makeCommonColumn<RatingItem>(info),
  size: 197,
};

const makeOrganizationColumns = <T extends RatingItem>({ filters }: { filters: TFilters }) => [
  idColumn,
  {
    accessorKey: 'organizationName',
    header: 'Название организации',
    enableSorting: false,
    cell: (info) => makeOrganizationNameColumn<T>(info),
    size: 220,
  },
  ratingColumn,
  reviewCountColumn({ filters }),
  responseCountColumn,
  solvedCountColumn,
];

const makeProductColumns = ({ ratings, filters }: { ratings: ProductRatingItem[]; filters: TFilters }) => {
  let termsCount: { [key: string]: number } = {};

  ratings.forEach((rating) => {
    Object.values(rating.termsByKey || {}).forEach(({ key }) => {
      termsCount = {
        ...termsCount,
        [key]: (termsCount[key] ?? 0) + 1,
      };
    });
  });

  const sortedColumnsKeys = Object.entries(termsCount)
    .sort(sortTermsByPopularity)
    .slice(0, 2)
    .map((item: [string, number]) => item[0]);

  const columnsFromTerms = sortedColumnsKeys.map((key) => {
    const description = ratings.find((rating) => rating.termsByKey[key])?.termsByKey[key].description;

    return {
      accessorKey: key,
      header: _upperFirst(description),
      cell: (info) => makeProductTermColumn(key)(info),
      enableSorting: false,
    };
  });

  const additionalColumns = [
    ...columnsFromTerms,
    ...[solvedCountColumn, responseCountColumn].slice(0, 2 - columnsFromTerms.length),
  ];

  const columns = [
    idColumn,
    {
      accessorKey: 'productName',
      header: 'Название продукта',
      enableSorting: false,
      cell: (info) => makeProductNameColumn(filters)(info),
      size: 220,
    },
    ratingColumn,
    ...additionalColumns,
    reviewCountColumn({ filters }),
  ];

  return columns;
};

export const makeConfig = <T = OrganizationRatingItem[] | ProductRatingItem[]>(
  ratings: T[],
  filters: TFilters,
): UseTableProps<T> => {
  const { ratingType } = filters;

  let columns = null;

  if (ratingType === RATING_TYPES.Organizations) {
    columns = makeOrganizationColumns({ filters });
  } else if (ratingType === RATING_TYPES.Products) {
    columns = makeProductColumns({ ratings: ratings as unknown as ProductRatingItem[], filters });
  }

  return {
    data: ratings,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    enableGlobalFilter: true,
    columns,
  };
};
